import { absoluteCentre, marginBottom, marginTop, typography } from "../../common/styles";
import { ActionType, CompleteRecurringBookingStepPayload } from "../../AppDataProvider/actions";
import { AppContext, Booking } from "../../AppDataProvider/AppDataProvider";
import { LoadingPill as BaseLoadingPill, NavButtons, Notification, Pill } from "../../common/components";
import { getCurrencyDisplay, requireBookingDataFields } from "../../utils";
import React, { ReactElement, useContext, useState } from "react";
import { RecurringBookingStepBookingData, RequiredFields } from "./types";
import { applyOpacity } from "../../common/styles/utils";
import { didUserSelectSameBookings } from "./utils/didUserSelectSameBookings";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { List } from "./List/List";
import styled from "styled-components";
import { useCreateReservationRecurringBookingMutation } from "../../GeneratedGraphQL";
import { useDeleteExistingReservedSpace } from "../Availability/hooks/useDeleteExistingReservedSpace";
import { useTranslation } from "react-i18next";
export function RecurringBookingStep(): ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    appDataDispatch,
    appData
  } = useContext(AppContext);
  const [error, setError] = useState<string | null>();
  const defaultSelectedBookings = appData.bookingData.selectedBookings ?? [];
  const [selectedBookings, setSelectedBookings] = useState<Booking[]>(defaultSelectedBookings);
  const [isSelectAllBookingsToggleChecked, setIsSelectAllBookingsToggleChecked] = useState<boolean>(false);
  const [createReservationLoading, setCreateReservationLoading] = useState<boolean>(false);
  const [createRecurringBookingReservation] = useCreateReservationRecurringBookingMutation({
    errorPolicy: "all"
  });
  requireBookingDataFields<RecurringBookingStepBookingData>(appData.bookingData, [...RequiredFields]);
  const {
    bookingData
  } = appData;
  const {
    attendees,
    createdSource,
    duration,
    organisation,
    recurringBooking,
    reservedSpaceId
  } = bookingData;

  // If no reserved space exists, this function does nothing, so it can be
  // safely called unconditionally.
  const deleteExistingReservedSpace = useDeleteExistingReservedSpace({
    organisationId: organisation._id,
    reservedSpaceId
  });
  const {
    recurring
  } = recurringBooking;
  const {
    bookings
  } = recurring;
  const {
    recurrenceType
  } = recurring;
  const bookingSelectionEnabled = recurrenceType == "INDIVIDUAL_BOOKINGS";
  const toggleIsAllBookingsChecked = () => {
    const bookingsWithRemainingSpaces = bookings.filter(booking => booking.remainingSpaces > 0);
    if (!isSelectAllBookingsToggleChecked) {
      setSelectedBookings([...bookingsWithRemainingSpaces]);
    } else {
      setSelectedBookings([]);
    }
    setError(undefined);
    setIsSelectAllBookingsToggleChecked(!isSelectAllBookingsToggleChecked);
  };
  const selectBooking = (selectedBooking: Booking) => {
    const isBookingSelected = selectedBookings.includes(selectedBooking);
    const updatedSelectedBookings = isBookingSelected ? selectedBookings.filter(booking => booking._id !== selectedBooking._id) : [...selectedBookings, selectedBooking];
    setError(undefined);
    setIsSelectAllBookingsToggleChecked(false);
    setSelectedBookings(updatedSelectedBookings);
  };
  const goBack = () => {
    // We don't want to delete reservations for non-recurring bookings when we
    // go back from this step, only recurring ones.
    if (bookingData.selectedBookings?.length) {
      void deleteExistingReservedSpace();
    }
    appDataDispatch({
      type: ActionType.BACK_FROM_RECURRING_BOOKING_STEP
    });
  };
  const completeStep = async () => {
    const bookingsForPayload = !bookingSelectionEnabled ? bookings : selectedBookings;
    if (bookingsForPayload.length === 0) {
      setError(t("errors.noBookingsSelected"));
      return;
    }
    if (reservedSpaceId && didUserSelectSameBookings({
      bookingDataSelectedBookings: bookingData.selectedBookings,
      stepSelectedBookings: bookingsForPayload
    })) {
      appDataDispatch({
        type: ActionType.COMPLETE_RECURRING_BOOKING_STEP
      });
      return;
    }
    setCreateReservationLoading(true);

    /**
     * Add 1 minute to reservation expiry on the back end.
     * This buffer means front-end will prompt for a reload 1 minute before
     * actual expiry, avoiding situations where user is interacting at same
     * time as reservation expiry.
     * Chronos only accepts full-minute values so 1 minute is easiest compromise
     */
    const {
      reservationExpiryMinutes
    } = organisation;
    const expiresAfter = reservationExpiryMinutes + 1;
    const variables = {
      input: {
        bookingIds: bookingsForPayload.map(booking => booking._id),
        expiresAfter,
        organisationId: organisation._id,
        recurringId: recurring.id,
        requiredSpaces: attendees,
        source: createdSource
      }
    };
    const {
      data,
      errors
    } = await createRecurringBookingReservation({
      variables
    });
    if (errors?.some(error => error.message === "Response code 422 (Unprocessable Entity)")) {
      appDataDispatch({
        payload: {
          buttonLabel: t("reload"),
          content: t("errors.somethingChanged"),
          icon: faExclamationTriangle,
          onClick: /* istanbul ignore next */() => location.reload(),
          title: t("somethingChanged")
        },
        type: ActionType.SHOW_MODAL_DIALOG
      });
      return;
    } else if (!data || errors) {
      setError(t("errors.cannotCreateRecurringReservation"));
      setCreateReservationLoading(false);
      return;
    }
    const {
      reservedSpace
    } = data.createReservationForRecurringBooking;

    // Wait until after confirming the new reservation exists before deleting
    // any existing reservation
    await deleteExistingReservedSpace();
    setCreateReservationLoading(false);

    /**
     * Use the earliest start from the user-selected bookings as the
     * confirmation page's start date. The parent reservation's `start`
     * field reflects the start of the recurring series on the back end
     * rather than the specific occurrence the customer picked, which
     * caused the booking confirmation page to display a date other than
     * the one selected (e.g. selecting 11 Feb but seeing 18 Feb).
     */
    const earliestStartTime = Math.min(...bookingsForPayload.map(booking => new Date(booking.start).getTime()));
    const payload: CompleteRecurringBookingStepPayload = {
      updatedData: {
        reservedSpaceId: reservedSpace._id,
        selectedBookings: bookingsForPayload,
        startDate: new Date(earliestStartTime)
      }
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_RECURRING_BOOKING_STEP
    });
  };
  const durationPrice = duration.price ?? 0;
  const getSelectedRecurringBookingTotalPrice = (): number => {
    return selectedBookings.reduce((total, booking) => {
      const {
        price
      } = booking;
      const bookingPrice = typeof price !== "undefined" && price !== null ? price : durationPrice;
      return total + bookingPrice * attendees;
    }, 0);
  };
  const getRecurringBookingTotalPrice = (): number => {
    return bookings.reduce((total, booking) => {
      const {
        price
      } = booking;
      const bookingPrice = typeof price !== "undefined" && price !== null ? price : durationPrice;
      return total + bookingPrice * attendees;
    }, 0);
  };
  const getRecurringBookingDisplayTotal = (): string => {
    const totalPrice = bookingSelectionEnabled ? getSelectedRecurringBookingTotalPrice() : getRecurringBookingTotalPrice();
    return getCurrencyDisplay(totalPrice, organisation.currency, i18n.language);
  };
  const showBookingPrice = bookings.some(booking => (booking.price !== undefined && booking.price !== null ? booking.price : durationPrice) > 0);
  const showTotalPrice = getRecurringBookingTotalPrice() > 0 && showBookingPrice;
  return <Container data-sentry-element="Container" data-sentry-component="RecurringBookingStep" data-sentry-source-file="RecurringBookingStep.tsx">
      <Notification variant="recurrenceInfo" data-sentry-element="Notification" data-sentry-source-file="RecurringBookingStep.tsx">
        {bookingSelectionEnabled ? t("serviceRepeatsSelectDates") : t("serviceRepeats")}
      </Notification>

      {error && <ErrorNotification variant="warning">{error}</ErrorNotification>}

      <List selectedBookings={selectedBookings} isSelectAllBookingsToggleChecked={isSelectAllBookingsToggleChecked} onSelectBooking={selectBooking} onToggleAllBookingsChecked={toggleIsAllBookingsChecked} data-sentry-element="List" data-sentry-source-file="RecurringBookingStep.tsx" />
      {showTotalPrice && <TotalPriceContainer>
          <Label>{t("total")}</Label>
          <Pill small>{getRecurringBookingDisplayTotal()}</Pill>
        </TotalPriceContainer>}

      {createReservationLoading && <LoadingPill />}

      <NavButtons {...{
      onBack: goBack,
      onContinue: () => void completeStep()
    }} data-sentry-element="NavButtons" data-sentry-source-file="RecurringBookingStep.tsx" />
    </Container>;
}
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const ErrorNotification = styled(Notification)`
  ${marginTop.xs}
`;
const Label = styled.label`
  ${typography.bodyBold};
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6, false)};
  &::first-letter {
    text-transform: capitalize;
  }
`;
const TotalPriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${marginTop.m}
  ${marginBottom.m}
`;
const LoadingPill = styled(BaseLoadingPill)`
  ${absoluteCentre}
  position: fixed;
`;
export default RecurringBookingStep;